import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type NavigatedFrom from '@alltrails/amplitude/enums/NavigatedFrom';
import type TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import isAndroid from '@alltrails/core/utils/isAndroid';
import isIos from '@alltrails/core/utils/isIos';
import type ShareModal from '../components/ShareModal/ShareModal';
import type { ShareObject } from '../types/ShareObject';
import logShareDisplayedForObject from '../utils/logShareDisplayedForObject';
import useShareUrl from './useShareUrl';

type Args = {
  listType?: string;
  navigatedFrom?: NavigatedFrom;
  shareObject: ShareObject;
  trailPageType?: TrailPageType;
};

type UseSharing = {
  closeShare: () => void;
  isShareModalOpen: boolean;
  openShare: () => void;
  shareModalProps: Pick<ComponentProps<typeof ShareModal>, 'isOpen' | 'listType' | 'navigatedFrom' | 'onClose' | 'shareObject' | 'trailPageType'>;
};

/**
 * Single entry point for sharing. On iOS and Android this opens the native share sheet; everywhere
 * else — and whenever the native sheet is unavailable or errors — it falls back to the ShareModal.
 *
 * Spread `shareModalProps` onto the modal and render it behind `isShareModalOpen`.
 */
export default function useSharing({ listType, navigatedFrom, shareObject, trailPageType }: Args): UseSharing {
  const intl = useIntl();
  const { shareUrl } = useShareUrl(shareObject);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const closeShare = useCallback(() => setIsShareModalOpen(false), []);

  const openShare = useCallback(() => {
    if ((isIos() || isAndroid()) && navigator.share) {
      const title = intl.formatMessage({ defaultMessage: '{name} on AllTrails' }, { name: shareObject.name });

      try {
        const sharePromise = navigator.share({ title, url: shareUrl });
        logShareDisplayedForObject({ listType, navigatedFrom, shareObject, trailPageType });

        sharePromise.catch((error: unknown) => {
          // If the user cancels the share, don't show the modal
          if (error instanceof Error && error.name === 'AbortError') {
            return;
          }
          setIsShareModalOpen(true);
        });
        return;
      } catch {
        // Fallback to the modal below
      }
    }

    setIsShareModalOpen(true);
  }, [intl, listType, navigatedFrom, shareObject, shareUrl, trailPageType]);

  const shareModalProps = useMemo(
    () => ({ isOpen: isShareModalOpen, listType, navigatedFrom, onClose: closeShare, shareObject, trailPageType }),
    [closeShare, isShareModalOpen, listType, navigatedFrom, shareObject, trailPageType]
  );

  return { closeShare, isShareModalOpen, openShare, shareModalProps };
}
